import { useMemo, useState } from 'react'
import styled from 'styled-components'

import { CreatorPairsTable } from '@root/features/services/creator-pairs/ui/creator-pairs-table/creator-pairs-table'
import { reatomTradesTable } from '@root/features/trade/tables/trades-table/atoms'
import { TradesTable } from '@root/features/trade/tables/trades-table/ui/trades-table'
import { useTranslation } from '@root/shared/localization'
import { UnderlineTabs } from '@root/shared/ui-kit'

import { WalletProfileActivity } from './wallet-profile-activity'
import { WalletProfileDegenHours } from './wallet-profile-degen-hours'
import { WalletProfilePositions } from './wallet-profile-positions'
import { WalletProfileTransfers } from './wallet-profile-transfers'

import type { WalletProfile } from '../model'
import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'
import type { TransKey } from '@root/shared/localization'

/**
 * The lists under a wallet's figures.
 *
 * Each is a table the app already has, pointed at one address instead of at a
 * pair: the trades table can take a list of wallets and no pair, which is a
 * wallet's history across everything it has touched, and the creator table
 * takes an address directly.
 *
 * Only the selected one is mounted, so opening the profile pays for the
 * positions and nothing else. The rest load when they are asked for, which is
 * what keeps the modal inside its budget however many lists it grows.
 */

type Tab =
  | 'positions'
  | 'history'
  | 'top'
  | 'activity'
  | 'dev-tokens'
  | 'transfers'
  | 'degen-hours'

const TABS: ReadonlyArray<{ value: Tab; label: TransKey }> = [
  { value: 'positions', label: 'wallet-profile.tab.positions' },
  { value: 'history', label: 'wallet-profile.tab.history' },
  { value: 'top', label: 'wallet-profile.tab.top' },
  { value: 'activity', label: 'wallet-profile.tab.activity' },
  { value: 'dev-tokens', label: 'wallet-profile.tab.dev-tokens' },
  { value: 'transfers', label: 'wallet-profile.tab.transfers' },
  { value: 'degen-hours', label: 'wallet-profile.tab.degen-hours' },
]

/** What "still holding it" means: anything left, however little. */
const isOpen = (position: WalletProfile.Position) =>
  position.remainingTokens > 0

type Props = {
  walletAddress: Address
  network: Network.Network
  positions: ReadonlyArray<WalletProfile.Position>
}

export const WalletProfileTabs: React.FC<Props> = ({
  walletAddress,
  network,
  positions,
}) => {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('positions')

  const trades = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomTradesTable({
        pairAddress: undefined,
        pair: undefined,
        wallets: [walletAddress],
      }),
    [walletAddress],
  )

  const open = useMemo(() => positions.filter(isOpen), [positions])

  return (
    <Root>
      <Tabs value={tab} onChange={(value) => setTab(value as Tab)}>
        {TABS.map(({ value, label }) => (
          <UnderlineTabs.Tab key={value} value={value}>
            {t(label)}
          </UnderlineTabs.Tab>
        ))}
      </Tabs>

      <Panel>
        {tab === 'positions' ? (
          <WalletProfilePositions positions={open} />
        ) : undefined}
        {/* Everything it has traded, best first, not only what is still held. */}
        {tab === 'top' ? (
          <WalletProfilePositions positions={positions} />
        ) : undefined}
        {tab === 'history' ? (
          <Trades model={trades} pair={undefined} />
        ) : undefined}
        {tab === 'dev-tokens' ? (
          <CreatorPairsTable creatorAddress={walletAddress} network={network} />
        ) : undefined}
        {tab === 'activity' ? (
          <WalletProfileActivity
            walletAddress={walletAddress}
            network={network}
          />
        ) : undefined}
        {tab === 'transfers' ? (
          <WalletProfileTransfers
            walletAddress={walletAddress}
            network={network}
          />
        ) : undefined}
        {tab === 'degen-hours' ? (
          <WalletProfileDegenHours
            walletAddress={walletAddress}
            network={network}
          />
        ) : undefined}
      </Panel>
    </Root>
  )
}

/**
 * A card, like the blocks above it.
 *
 * The lists used to sit loose on the dialog's background while everything
 * above them was bordered and padded, so the tab bar read as belonging to the
 * block on its left and the rows ran into the dialog's edge.
 */
const Root = styled.section`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  border: 1px solid var(--background-3);
  border-radius: 12px;
  background: var(--background-1);
`

const Tabs = styled(UnderlineTabs)`
  --tab-font-size: 12px;
  --tab-padding-inline: 12px;

  /* Its own rule across the whole card, not a line under four of six tabs. */
  border-bottom: 1px solid var(--background-3);

  /* Six names do not fit a phone; they scroll rather than wrap into two
     rows that push the list off the screen. */
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const Panel = styled.div`
  overflow: auto;
  display: grid;
  align-content: start;

  min-height: 280px;
  padding: 12px 14px 14px;
`

const Trades = styled(TradesTable)`
  min-height: 280px;
`
